import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { numStr, parseNum } from "@/lib/format";
import { Button, Field, Input, Segmented, Switch } from "@/components/kit";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { monthLabel } from "@/lib/dre";

const SCOPES = [{ value: "month", label: "Só neste mês" }, { value: "all", label: "Deste mês em diante" }];

export function ExpenseDialog({ open, onOpenChange, month, groups, expense }) {
  const qc = useQueryClient();
  const [f, setF] = useState(() => ({ name: expense?.name || "", group: expense?.group || "pessoal", amount: expense ? numStr(expense.amount) : "", recurring: false, scope: "month" }));
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  const save = useMutation({
    mutationFn: () => {
      const body = { name: f.name.trim(), group: f.group, amount: parseNum(f.amount), month };
      if (!expense) return api("/dre/expenses", { method: "POST", body: { ...body, recurring: f.recurring } });
      return api(`/dre/expenses/${expense.id}`, { method: "PUT", body: { ...body, scope: f.scope } });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["dre"] }); toast.success(expense ? "Despesa atualizada" : "Lançamento adicionado"); onOpenChange(false); },
    onError: (e) => toast.error(e.message),
  });
  const submit = () => (!f.name.trim() ? toast.error("Informe a descrição") : save.mutate());
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-[var(--surface)] text-[var(--text)] border-[var(--border)] rounded-2xl" data-testid="dre-expense-dialog">
        <DialogTitle>{expense ? "Editar lançamento" : "Novo lançamento"} · {monthLabel(month)}</DialogTitle>
        <div className="flex flex-col gap-3">
          <Field label="Descrição"><Input autoFocus placeholder="Ex.: Aluguel, salários, energia" value={f.name} onChange={set("name")} data-testid="dre-expense-name" /></Field>
          <Field label="Grupo no DRE">
            <select className="input" value={f.group} onChange={set("group")} data-testid="dre-expense-group">
              {Object.entries(groups).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </Field>
          <Field label="Valor (R$)"><Input inputMode="decimal" placeholder="0,00" value={f.amount} onChange={set("amount")} data-testid="dre-expense-amount" /></Field>
          {!expense && (
            <div className="flex items-center gap-4">
              <div className="flex-1"><div className="label">Repete todo mês</div><div className="caption">Despesa fixa, a partir de {monthLabel(month)}.</div></div>
              <Switch on={f.recurring} onChange={set("recurring")} data-testid="dre-expense-recurring" />
            </div>
          )}
          {expense?.kind === "fixa" && <Field label="Aplicar o novo valor"><Segmented options={SCOPES} value={f.scope} onChange={set("scope")} testid="dre-expense-scope" /></Field>}
        </div>
        <Button loading={save.isPending} onClick={submit} data-testid="dre-expense-save">Salvar</Button>
      </DialogContent>
    </Dialog>
  );
}
